/* Dice Goblin: reserve a library game (snippets/library-reserve.liquid)
   <library-reserve> on a library copy's page says whether the game is on the shelf, and lets a member reserve it
   (POST /library/holds, with the game's picture). Round 7 (Mo): the Lair holds it until midnight at the end of the
   third day, the day it's made counting as the first (made any time Tuesday, held until midnight Thursday; the app's
   until is 00:00 Friday, which reads "midnight, Thu 8 Oct"), and emails the shop and the member. If nobody collects
   it, it goes back on the shelf by itself. What it shows:
   - On the shelf: how many copies, and for members "Reserve this game" with when we'd hold it until and how to collect
     it. Logged out: "Log in to reserve" (back to this page). Logged in without a plan: "Join the library to reserve
     games", to the plans.
   - Every copy reserved by someone else: "Reserved right now" and when it's back on the shelf if nobody collects it.
     Every copy at home with members (round 7, out): "Out on loan right now".
   - Theirs: when we're holding it until, "Cancel my reservation" (it asks first) and a link to My Library.
   - At home with them (round 7, atHome): that they've got it, and (16 Oct 2026) that to return it they bring it to
     the counter, where staff scan it back onto the shelf.
   - When the Lair app says no (their plan's limit, say), its own words.
   The words come through Shopify's t filter, which escapes HTML (We&#39;re), so they're decoded once as they're read
   and treated as plain text after that (contract v7, section 17).
   The status is GET /library/status?ids=<variant id>, loaded again when the page comes back into view after a minute
   away. A hold's time is information, not a countdown: nothing ticks. Changes are announced to screen readers, and
   focus stays in the block after reserving or cancelling (on the Reserve button after a cancel).
   Needs lair-core.js (window.Lair: the store and its backend, the live Lair app or the demo) and lair-config.
   Round 17 (contract v17-help-me-pick, section 9.4), under the block: "Add to my queue" for a library member (never for
   a game that stays in the Lair: POST /me/queue, and once it's in, where it is in their queue and a link to it; when the
   queue holds it for them straight away, as the Lair does with a copy free and room on their plan, the block shows
   it's theirs instead, and a hold that goes by post says to confirm the postage in My Library), "Tell
   me when it's back" while no copy is free (POST /library/watch, or Stop watching), and "Played it?" thumbs for anyone
   logged in (POST /library/rate, source 'page'; a second tap on the same thumb takes it off). Logged out, the thumbs and
   Tell me are a link to log in and come back. Where they stand comes from GET /me/queue (queue and watching) and GET
   /me/picks (ratings), asked once a visit. */
(() => {
  'use strict';
  if (customElements.get('library-reserve')) return;

  const fill = (text, values = {}) => String(text || '').replace(/\[(\w+)\]/g, (_, key) => (values[key] ?? ''));
  const escape = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  /** Shopify's t filter escapes the words (We&#39;re): decode the five entities once (contract v7, section 17). Shell's
      Lair.decodeText when lair-core.js has it, else this exact copy. */
  const decode = (s) => String(s ?? '').replace(/&(amp|lt|gt|quot|#39|#x27);/g, (m, e) => ({ amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", '#x27': "'" })[e]);
  const decodeText = (s) => ((window.Lair && window.Lair.decodeText) || decode)(s);
  const HOLD_DAYS = 3; // the day it's made counts as the first: until 00:00 three days on

  class LibraryReserve extends HTMLElement {
    connectedCallback() {
      if (this.started) {
        // moved in the page: listen again, and look again if it's been a while
        document.addEventListener('visibilitychange', this.onVisible);
        this.onVisible();
        return;
      }
      this.started = true;
      this.body = this.querySelector('[data-reserve-body]');
      this.sayBox = this.querySelector('[data-reserve-say]');
      try {
        const raw = JSON.parse(this.querySelector('[data-reserve-words]')?.textContent || '{}') || {};
        this.words = Object.fromEntries(Object.entries(raw).map(([key, value]) => [key, decodeText(value)]));
      } catch {
        this.words = {};
      }
      this.icon = this.querySelector('template[data-reserve-icon]')?.innerHTML.trim() || '';
      this.lair = window.Lair || null;
      this.variantId = String(this.dataset.variantId || '');
      this.copies = Math.min(10, Math.max(1, Math.floor(Number(this.dataset.copies)) || 1));
      this.title = this.dataset.title || '';
      this.status = null;
      this.state = null;
      this.asking = false;
      this.busy = false;
      this.problem = null; // { text, link: 'login' | 'join' | null }: what the Lair app said no with
      this.addEventListener('click', (event) => this.onClick(event));
      // Back on the page after a while (it may have been reserved, or a hold may have ended): look again
      this.onVisible = () => {
        if (!document.hidden && Date.now() - (this.loadedAt || 0) > 60000) this.load();
      };
      document.addEventListener('visibilitychange', this.onVisible);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.onVisible);
    }

    get backend() {
      return this.lair && this.lair.store ? this.lair.store.backend : null;
    }

    get customer() {
      return this.lair && this.lair.store ? this.lair.store.cfg.customer || null : null;
    }

    /** Games their plan lets them have reserved at once: the Lair's answer once GET /me has said (round 10: the Lair
        bills library memberships itself, so it knows), else lair-config's from their tags. 0 without a plan. */
    get plan() {
      const c = this.customer;
      if (!c) return 0;
      if (this.lairPlan !== undefined) return this.lairPlan ? Math.max(0, Math.floor(Number(this.lairPlan.games)) || 0) : 0;
      const games = Math.floor(Number(c.libraryGames));
      if (Number.isFinite(games)) return Math.max(0, games);
      return c.member ? 1 : 0;
    }

    /** Round 10: their library plan from the Lair (GET /me's library, shared with anything else on the page that asks),
        once a visit. A plan whose payment is outstanding is blocked: borrowing and reserving pause. Members' parts of the
        page's borrow card show, and its "Join" parts go, once it says they're a member. */
    async askPlan() {
      const backend = this.backend;
      if (this.planAsked || !this.customer || !backend || typeof backend.me !== 'function') return;
      this.planAsked = true;
      try {
        const me = (await backend.me()) || {};
        if (!me.library || typeof me.library !== 'object') return; // a Lair app from before round 7: the tags stand
        const plan = me.library.plan && Number(me.library.plan.games) > 0 ? me.library.plan : null;
        this.lairPlan = plan;
        document.querySelectorAll('[data-borrow-join]').forEach((el) => { el.hidden = Boolean(plan); });
        if (this.state) this.show(this.state);
      } catch {
        // the tags stand for now; the next look at the shelf (back on the page after a while) asks again
        this.planAsked = false;
      }
    }

    /** A hold's until, in Lair time (contract v7, section 6): exactly midnight is the end of the day before, "midnight,
        Thu 8 Oct"; any other time (holds made before round 7) as round 6, "Thu 8 Oct, 12pm" */
    when(ms) {
      const t = this.lair.store.time;
      const minutes = t.minutesOf(ms);
      if (minutes === 0) return `midnight, ${t.fmtDate(t.addDays(t.key(ms), -1)).replace(',', '')}`;
      const h = Math.floor(minutes / 60) % 24;
      const mi = minutes % 60;
      return `${t.fmtDate(t.key(ms)).replace(',', '')}, ${h % 12 || 12}${mi ? `:${String(mi).padStart(2, '0')}` : ''}${h < 12 ? 'am' : 'pm'}`;
    }

    /** When a hold made now would end: 00:00 Lair time three days on, the end of the third day counting today */
    holdEnds() {
      const t = this.lair.store.time;
      return t.at(t.addDays(t.today(), HOLD_DAYS), 0);
    }

    /** When a game went home with them: "today", "yesterday" or "on Sat 3 Oct" */
    borrowedOn(ms) {
      const t = this.lair.store.time;
      const key = t.key(ms);
      if (key === t.today()) return this.words.today || 'today';
      if (key === t.addDays(t.today(), -1)) return this.words.yesterday || 'yesterday';
      return fill(this.words.onDay || 'on [day]', { day: t.fmtDate(key).replace(',', '') });
    }

    /** What's on the shelf. The Lair app knows the holds; until it can ask Shopify, it may not know every copy this page
        knows about (it counts 1 for a game it hasn't seen), so the copies it doesn't know of are on the shelf too. */
    counts(s = this.status || {}) {
      const known = Math.max(0, Math.floor(Number(s.copies)) || 0);
      const total = Math.max(known, this.copies);
      const available = Math.min(total, Math.max(0, Math.floor(Number(s.available)) || 0) + Math.max(0, this.copies - known));
      return { total, available, held: Math.max(0, Math.floor(Number(s.held)) || 0) };
    }

    stateOf(s = this.status) {
      if (!s) return 'down';
      if (s.atHome && s.atHome.id) return 'home';
      if (s.mine && s.mine.id) return 'mine';
      if (this.counts(s).available > 0) return 'available';
      if (Number(s.nextFree) > 0) return 'reserved';
      return Math.floor(Number(s.out)) > 0 ? 'out' : 'none';
    }

    async load({ quiet = false } = {}) {
      const backend = this.backend;
      if (!backend || typeof backend.libraryStatus !== 'function' || !this.variantId) {
        this.show('down');
        return;
      }
      const ask = (this.asked = (this.asked || 0) + 1);
      this.askPlan();
      try {
        const data = await backend.libraryStatus([this.variantId]);
        if (ask !== this.asked) return;
        const s = data && data.games ? data.games[this.variantId] : null;
        if (!s) throw new Error('The Lair app sent nothing for this game.');
        const before = this.state;
        this.status = s;
        this.loadedAt = Date.now();
        const state = this.stateOf(s);
        // a change from elsewhere (another tab, another member, a hold that ended) is read out; an action here says its own
        if (state !== before) {
          this.asking = false;
          if (!quiet) this.problem = null;
        }
        this.show(state);
        if (before && state !== before && !quiet) this.say(this.headline(state));
      } catch (error) {
        if (ask !== this.asked) return;
        console.warn('Lair: the library status did not load', error);
        this.show('down');
      }
    }

    /** The status line's words for a state */
    headline(state) {
      const w = this.words;
      if (state === 'home') return w.home;
      if (state === 'mine') return w.mine;
      if (state === 'reserved') return Math.floor(Number((this.status || {}).out)) > 0 ? w.reservedOut || w.reserved : w.reserved;
      if (state === 'out') return w.out;
      if (state === 'none') return w.none;
      if (state === 'down') return w.down;
      const { total, available } = this.counts();
      if (available < total) return fill(w.copiesSome, { count: available, total });
      return fill(available === 1 ? w.copiesOne : w.copiesOther, { count: available });
    }

    show(state, { focus = null } = {}) {
      this.state = state;
      this.dataset.state = state;
      const html = this.html(state);
      if (html !== this.drawn) {
        this.drawn = html;
        this.body.innerHTML = html;
      }
      this.body.removeAttribute('aria-busy');
      this.showMore(); // round 17
      if (focus) {
        const el = this.querySelector(focus) || this.querySelector('[data-reserve-status]');
        el?.focus({ preventScroll: true });
        if (el && el.getBoundingClientRect().bottom > window.innerHeight) el.scrollIntoView({ block: 'nearest' });
      }
    }

    html(state) {
      const w = this.words;
      const s = this.status || {};
      const status = (text, icon = false) => `<p class="library-reserve__status" tabindex="-1" data-reserve-status>${
        icon && this.icon ? this.icon : '<span class="library-reserve__dot" aria-hidden="true"></span>'}<span>${escape(text)}</span></p>`;
      const stamp = (text, ms) => {
        const [before, after = ''] = String(text || '').split('[when]');
        return `<p class="library-reserve__text">${escape(before)}<strong class="library-reserve__when">${escape(this.when(ms))}</strong>${escape(after)}</p>`;
      };
      const links = {
        login: [this.dataset.loginUrl, w.login],
        join: [this.dataset.joinUrl, w.join],
        lair: [this.dataset.lairUrl, w.myHolds],
      };
      const lairLink = `<a class="text-link" href="${escape(this.dataset.lairUrl)}">${escape(w.myLair)}</a>`;
      const fix = this.problem && links[this.problem.link] ? links[this.problem.link] : null;
      const problem = this.problem
        ? `<p class="form-message form-message--error library-reserve__error" role="alert"><span>${escape(this.problem.text)}${
          fix ? ` <a class="text-link" href="${escape(fix[0])}">${escape(fix[1])}</a>` : ''}</span></p>`
        : '';
      if (state === 'down') {
        return `<p class="library-reserve__text library-reserve__down">${escape(w.down)}</p>
          <div class="library-reserve__actions"><button class="text-link" type="button" data-reserve-retry>${escape(w.retry)}</button></div>`;
      }
      if (state === 'home') {
        return `${status(w.home, true)}<p class="library-reserve__text">${escape(fill(w.homeText, { when: this.borrowedOn(Number(s.atHome.outAt)) }))}</p>
          <div class="library-reserve__actions">${lairLink}</div>${problem}`;
      }
      if (state === 'mine') {
        const ask = this.asking
          ? `<div class="library-reserve__ask" role="group" aria-labelledby="reserve-ask-${escape(this.variantId)}">
              <p id="reserve-ask-${escape(this.variantId)}">${escape(w.ask)}</p>
              <div class="cluster">
                <button class="button button--ruby button--small" type="button" data-reserve-cancel-yes>${escape(w.askYes)}</button>
                <button class="button button--ghost button--small" type="button" data-reserve-cancel-no>${escape(w.askNo)}</button>
              </div>
            </div>`
          : `<div class="library-reserve__actions">
              <button class="button button--ghost button--small" type="button" data-reserve-cancel>${escape(w.cancel)}</button>
              ${lairLink}
            </div>`;
        // round 17: held by their queue to go by post: its postal swap's cutoff (the hold runs 30 minutes longer for a
        // payment in flight) and nothing to collect; paid, the Thursday it goes out. Ready to post in My Library has its
        // buttons (Send it, Not this time), so there's no Cancel here.
        const send = this.more && this.more.postHolds ? this.more.postHolds.get(String(s.mine.id)) || null : null;
        if (send && w.postNote) {
          const t = this.lair.store.time;
          const paid = ['paid', 'packed'].includes(send.status);
          const line = paid
            ? `<p class="library-reserve__text">${escape(fill(w.postPaid, { day: send.sendDay ? t.fmtDate(send.sendDay).replace(',', '') : '' }))}</p>`
            : `${stamp(w.mineText, Number(send.confirmBy) || s.mine.until)}<p class="library-reserve__note">${escape(w.postNote)}</p>`;
          return `${status(w.mine, true)}${line}<div class="library-reserve__actions">${lairLink}</div>${problem}`;
        }
        return `${status(w.mine, true)}${stamp(w.mineText, s.mine.until)}
          <p class="library-reserve__note">${escape(w.collect)}</p>${ask}${problem}`;
      }
      if (state === 'reserved') return `${status(this.headline('reserved'))}${stamp(w.reservedText, Number(s.nextFree))}${problem}`;
      if (state === 'out') return `${status(w.out)}<p class="library-reserve__text">${escape(w.outText)}</p>${problem}`;
      if (state === 'none') return `${status(w.none)}<p class="library-reserve__text">${escape(w.noneText)}</p>${problem}`;
      // On the shelf: members reserve it; everyone else is told how
      const ends = this.when(this.holdEnds());
      const c = this.customer;
      let action;
      if (!c) action = `<a class="button button--ghost library-reserve__main" href="${escape(this.dataset.loginUrl)}">${escape(w.login)}</a>`;
      else if (!this.plan) action = `<a class="text-link" href="${escape(this.dataset.joinUrl)}" style="--link-accent: var(--c-mana)">${escape(w.join)}</a>`;
      else if (this.lairPlan && this.lairPlan.blocked) {
        // round 10: a payment outstanding pauses borrowing, so there's no Reserve until it's sorted
        action = `<p class="library-reserve__text">${escape(w.paused || 'Borrowing’s paused until your last library payment is sorted.')}</p>
          <a class="text-link" href="${escape(this.dataset.lairUrl)}" style="--link-accent: var(--c-mana)">${escape(w.pausedLink || 'Sort it in My Library')}</a>`;
      } else action = `<button class="button library-reserve__main" type="button" data-reserve>${escape(w.reserve)}</button>`;
      const member = Boolean(c && this.plan);
      const paused = Boolean(member && this.lairPlan && this.lairPlan.blocked);
      if (paused) return `${status(this.headline('available'))}<div class="library-reserve__actions library-reserve__actions--paused">${action}</div>${problem}`;
      return `${status(this.headline('available'))}
        <p class="library-reserve__text">${escape(fill(member ? w.offer : w.offerMembers, { when: ends }))}</p>
        <div class="library-reserve__actions">${action}</div>
        ${member ? `<p class="library-reserve__note">${escape(w.collect)}</p>` : ''}${problem}`;
    }

    /** Read something out once (the same words twice still count) */
    say(text) {
      const box = this.sayBox;
      if (!box || !text) return;
      box.textContent = '';
      window.setTimeout(() => {
        box.textContent = text;
      }, 60);
    }

    onClick(event) {
      const on = (selector) => event.target.closest(selector);
      let el;
      if ((el = on('[data-more]'))) {
        this.moreAct(el.dataset.more, el);
        return;
      }
      if ((el = on('[data-reserve]'))) this.reserve(el);
      else if (on('[data-reserve-cancel]')) {
        this.asking = true;
        this.problem = null;
        this.show(this.state, { focus: '[data-reserve-cancel-no]' });
      } else if (on('[data-reserve-cancel-no]')) {
        this.asking = false;
        this.show(this.state, { focus: '[data-reserve-cancel]' });
      } else if ((el = on('[data-reserve-cancel-yes]'))) this.cancel(el);
      else if (on('[data-reserve-retry]')) {
        this.body.setAttribute('aria-busy', 'true');
        this.load();
      }
    }

    busyOn(button) {
      this.busy = true;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }

    busyOff(button) {
      this.busy = false;
      if (!document.contains(button)) return;
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }

    async reserve(button) {
      const backend = this.backend;
      if (this.busy || !backend || typeof backend.reserveLibraryGame !== 'function') return;
      this.busyOn(button);
      this.problem = null;
      try {
        const result = await backend.reserveLibraryGame({
          variantId: this.variantId,
          productId: this.dataset.productId || null,
          title: this.title,
          shelfCode: this.dataset.shelfCode || '',
          handle: this.dataset.handle || '',
          copies: this.copies,
          image: this.dataset.image || null,
        });
        const hold = result && result.hold;
        const { available, held } = this.counts();
        this.status = {
          ...(this.status || {}), held: held + 1, available: Math.max(0, available - 1),
          mine: hold ? { id: hold.id, until: Number(hold.until) } : null,
        };
        this.busy = false;
        this.show(this.stateOf(), { focus: '[data-reserve-status]' });
        if (hold) this.say(fill(this.words.done, { title: this.title, when: this.when(Number(hold.until)) }));
        this.loadedAt = Date.now();
      } catch (error) {
        this.busyOff(button);
        const status = Number(error.status) || 0;
        // a way on from what the app said: log in, join, or (at their plan's limit) the games they've reserved
        this.problem = {
          text: (status && error.message) || this.words.failed,
          link: status === 401 ? 'login' : status === 403 ? 'join' : status === 409 ? 'lair' : null,
        };
        // Nothing left on the shelf (or theirs already, from another tab): show the shelf as it is now, which says it.
        // At their plan's limit the shelf hasn't changed, so the app's words stay underneath.
        if (status === 409) {
          await this.load({ quiet: true });
          if (this.state !== 'available') {
            this.problem = null;
            this.show(this.state);
          }
        } else this.show(this.state);
        (this.querySelector('[data-reserve]') || this.querySelector('[data-reserve-status]'))?.focus({ preventScroll: true });
      }
    }

    async cancel(button) {
      const backend = this.backend;
      const mine = this.status && this.status.mine;
      if (this.busy || !mine || !backend || typeof backend.cancelLibraryHold !== 'function') return;
      this.busyOn(button);
      this.problem = null;
      try {
        await backend.cancelLibraryHold(mine.id);
        this.asking = false;
        const { available, held } = this.counts();
        this.status = { ...this.status, mine: null, held: Math.max(0, held - 1), available: available + 1, nextFree: null };
        this.busy = false;
        // focus goes back where they started: the Reserve button
        this.show(this.stateOf(), { focus: '[data-reserve]' });
        this.say(fill(this.words.cancelled, { title: this.title }));
        this.load({ quiet: true });
      } catch (error) {
        this.busyOff(button);
        this.problem = { text: (Number(error.status) && error.message) || this.words.failed, link: null };
        this.asking = false;
        this.show(this.state, { focus: '[data-reserve-cancel]' });
      }
    }
  }

  /* ---------- Round 17: Add to my queue, Tell me when it's back and Played it? ---------- */
  const THUMBS = {
    up: '<path d="M7 10.5v9H4.5v-9zM7 10.5l3.6-6.3c1.4.2 2.2 1.2 2 2.6l-.5 3.2h5.2c1.3 0 2.2 1.2 1.9 2.4l-1.5 6c-.2.9-1 1.5-1.9 1.5H7"/>',
    down: '<path d="M7 13.5v-9H4.5v9zM7 13.5l3.6 6.3c1.4-.2 2.2-1.2 2-2.6l-.5-3.2h5.2c1.3 0 2.2-1.2 1.9-2.4l-1.5-6c-.2-.9-1-1.5-1.9-1.5H7"/>',
    queue: '<path d="M4 6.5h11M4 11.5h11M4 16.5h7M17.5 14v6M14.5 17h6"/>',
    bell: '<path d="M6.5 16.5V11a5.5 5.5 0 0111 0v5.5l1.5 1.5H5z"/><path d="M10 20a2 2 0 004 0"/>',
  };
  const svg = (name) => `<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${THUMBS[name]}</svg>`;
  /** The holds their queue made to go by post, with their postal swaps: GET /me/queue's sends still going, by hold id */
  const postHoldsOf = (q) => new Map((Array.isArray(q && q.sends) ? q.sends : [])
    .filter((x) => x && x.holdId && ['waiting', 'checkout', 'paid', 'packed'].includes(x.status)).map((x) => [String(x.holdId), x]));

  Object.assign(LibraryReserve.prototype, {
    /** Where they stand with this game: in their queue (and where), watching it, and their rating. Asked once a visit,
        and only when the backend knows round 17 (an older lair-core.js leaves the block as it was). */
    async loadMore() {
      const backend = this.backend;
      if (this.moreAsked || !this.customer || !backend || typeof backend.myQueue !== 'function') return;
      this.moreAsked = true;
      const productId = String(this.dataset.productId || '');
      const [queue, picks] = await Promise.allSettled([backend.myQueue(), typeof backend.myPicks === 'function' ? backend.myPicks() : Promise.resolve(null)]);
      const more = this.more || (this.more = {});
      if (queue.status === 'fulfilled' && queue.value) {
        const q = queue.value;
        const row = (Array.isArray(q.queue) ? q.queue : []).find((x) => String(x.productId) === productId);
        more.position = row ? Number(row.position) || null : null;
        more.watching = (Array.isArray(q.watching) ? q.watching : []).some((x) => String(x.productId) === productId);
        more.planned = Boolean(q.plan);
        more.postHolds = postHoldsOf(q);
      } else more.failed = true;
      if (picks.status === 'fulfilled' && picks.value && picks.value.ratings) more.rating = picks.value.ratings[productId] || null;
      more.ready = true;
      // their hold on it goes by post: the block says so (drawn again now that's known)
      if (this.state === 'mine' && more.postHolds && more.postHolds.size) this.show(this.state);
      else this.showMore();
    },

    /** No copy free for them to reserve: someone else's hold, or every copy out */
    noneFree() {
      return ['reserved', 'out', 'none'].includes(this.state);
    },

    showMore() {
      const box = this.querySelector('[data-reserve-more]');
      if (!box || this.state === 'down' || !this.state) {
        if (box) box.hidden = true;
        return;
      }
      if (this.customer && !this.moreAsked) this.loadMore();
      const html = this.moreHtml();
      if (html !== this.moreDrawn) {
        this.moreDrawn = html;
        box.innerHTML = html;
      }
      box.hidden = !html;
    },

    moreHtml() {
      const w = this.words;
      const more = this.more || {};
      const c = this.customer;
      const login = this.dataset.loginUrl;
      const busy = this.moreBusy ? ' disabled aria-busy="true"' : '';
      const parts = [];
      // Add to my queue: a library member (with a plan), never a game that stays in the Lair
      const member = Boolean(c) && this.plan > 0;
      if (member && this.dataset.inStoreOnly !== 'true' && !['home', 'mine'].includes(this.state)) {
        if (more.position) {
          parts.push(`<p class="library-reserve__row"><span class="library-reserve__text">${escape(fill(more.justAdded ? w.queueAdded : w.queueIn, { n: more.position }))}</span><a class="text-link" href="${escape(this.dataset.queueUrl)}" style="--link-accent: var(--c-mana)">${escape(w.queueSee)}</a></p>`);
        } else if (more.ready || !c) {
          parts.push(`<div class="library-reserve__row"><button class="button button--ghost button--small" type="button" data-more="queue"${busy}>${svg('queue')}${escape(w.queueAdd)}</button></div>`);
        }
      }
      // Tell me when it's back: no copy free (and it isn't theirs already)
      if (this.noneFree()) {
        if (!c) parts.push(`<div class="library-reserve__row"><a class="button button--ghost button--small" href="${escape(login)}">${svg('bell')}${escape(w.watchLogin)}</a></div>`);
        else if (more.watching) {
          parts.push(`<p class="library-reserve__row"><span class="library-reserve__text">${escape(w.watchOn)}</span><button class="text-link" type="button" data-more="unwatch" style="--link-accent: var(--c-ruby)"${busy}>${escape(w.watchStop)}</button></p>`);
        } else if (more.ready) parts.push(`<div class="library-reserve__row"><button class="button button--ghost button--small" type="button" data-more="watch"${busy}>${svg('bell')}${escape(w.watch)}</button></div>`);
      }
      // Played it? for anyone logged in; logged out, a link to log in and back
      if (!c) {
        parts.push(`<p class="library-reserve__played"><span class="library-reserve__played-label">${escape(w.played)}</span><a class="text-link" href="${escape(login)}">${escape(w.rateLogin)}</a></p>`);
      } else if (more.ready) {
        const thumb = (value) => `<button class="library-reserve__thumb library-reserve__thumb--${value}" type="button" data-more="rate" data-value="${value}" aria-pressed="${more.rating === value}"${this.moreBusy ? ' disabled' : ''}>${svg(value)}<span>${escape(value === 'up' ? w.up : w.down)}</span></button>`;
        parts.push(`<div class="library-reserve__played" role="group" aria-labelledby="played-${escape(this.variantId)}"><span class="library-reserve__played-label" id="played-${escape(this.variantId)}">${escape(w.played)}</span><span class="library-reserve__thumbs">${thumb('up')}${thumb('down')}</span></div>`);
      }
      if (!parts.length) return '';
      const said = more.say ? `<p class="${more.say.tone === 'error' ? 'form-message form-message--error' : 'library-reserve__note'} library-reserve__more-say">${escape(more.say.text)}</p>` : '';
      return `${parts.join('')}${said}`;
    },

    /** Add to my queue, watch, stop watching or rate: the Lair app's answer kept, said out loud, focus kept in the block */
    async moreAct(kind, button) {
      const backend = this.backend;
      if (this.moreBusy || !backend) return;
      const w = this.words;
      const more = this.more || (this.more = {});
      const productId = String(this.dataset.productId || '');
      // what the game's page knows of it, for a backend that hasn't seen it yet (the demo)
      const details = { variantId: this.variantId, title: this.title, handle: this.dataset.handle || '', shelfCode: this.dataset.shelfCode || '', image: this.dataset.image || null, inStoreOnly: this.dataset.inStoreOnly === 'true' };
      const value = button.dataset.value || null;
      this.moreBusy = true;
      more.say = null;
      this.showMore();
      let text = '';
      let focus = null;
      try {
        if (kind === 'queue') {
          const answer = (await backend.addToQueue(productId, details)) || {};
          const row = (Array.isArray(answer.queue) ? answer.queue : []).find((x) => String(x.productId) === productId);
          more.postHolds = postHoldsOf(answer);
          if (row && row.status === 'held' && row.hold) {
            // it was next, a copy was free and their plan had room, so the queue holds it for them straight away (by
            // post, until the postage cutoff): the block above shows it's theirs, from the Lair's library status
            more.position = null;
            text = more.postHolds.has(String(row.holdId)) ? w.queuePost : w.queueHeld;
            await this.load({ quiet: true });
            focus = '[data-reserve-status]';
          } else {
            more.position = row ? Number(row.position) || 1 : 1;
            more.justAdded = true;
            text = fill(w.queueAdded, { n: more.position });
            focus = '[data-reserve-more] a';
          }
        } else if (kind === 'watch') {
          await backend.watchGame(productId, details);
          more.watching = true;
          text = w.watchStarted;
          focus = '[data-more="unwatch"]';
        } else if (kind === 'unwatch') {
          await backend.unwatchGame(productId);
          more.watching = false;
          text = w.watchStopped;
          focus = '[data-more="watch"]';
        } else if (kind === 'rate') {
          const next = more.rating === value ? null : value;
          const answer = (await backend.rateGame(productId, next, 'page', details)) || {};
          more.rating = 'rating' in answer ? answer.rating : next;
          text = more.rating === 'up' ? w.ratedUp : more.rating === 'down' ? w.ratedDown : w.rateCleared;
          focus = `[data-more="rate"][data-value="${value}"]`;
        }
        more.say = { text, tone: 'ok' };
      } catch (error) {
        const status = Number(error && error.status) || 0;
        more.say = { text: (status && error.message) || w.moreFailed, tone: 'error' };
        focus = `[data-more="${kind}"]${value ? `[data-value="${value}"]` : ''}`;
      }
      this.moreBusy = false;
      this.showMore();
      this.say(more.say.text);
      (this.querySelector(focus) || this.querySelector('[data-reserve-more] button, [data-reserve-more] a'))?.focus({ preventScroll: true });
    },
  });

  customElements.define('library-reserve', LibraryReserve);
})();
